import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, CheckCircle2, Sparkles, CalendarDays, FileText, BarChart3, PlayCircle } from "lucide-react";
import { HERO_SLIDES as DEFAULT_SLIDES } from "@/data/content";
import { useSiteContent } from "@/context/SiteContentContext";

const MiniDash = () => (
  <div className="glass rounded-2xl p-5 shadow-2xl w-full max-w-md" data-testid="hero-dashboard-preview">
    <div className="flex items-center justify-between mb-4">
      <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Workspace · Illustrative</span>
      <span className="flex gap-1.5"><i className="h-2.5 w-2.5 rounded-full bg-[#db2777]" /><i className="h-2.5 w-2.5 rounded-full bg-[#f59e0b]" /><i className="h-2.5 w-2.5 rounded-full bg-[#10b981]" /></span>
    </div>
    <div className="grid grid-cols-7 gap-1.5 mb-4">
      {Array.from({ length: 14 }).map((_, i) => (
        <div key={i} className={`h-8 rounded-md ${[1, 3, 4, 8, 10, 12].includes(i) ? "bg-brand opacity-90" : "bg-foreground/5"}`} />
      ))}
    </div>
    <div className="space-y-2.5">
      {[["LinkedIn post · New chiller launch", "Pending approval", "#f59e0b"], ["Blog draft · Fire NOC checklist", "Approved", "#10b981"], ["Meta ad · AMC renewal offer", "Draft", "#06b6d4"]].map(([t, s, c]) => (
        <div key={t} className="flex items-center justify-between rounded-xl bg-foreground/5 px-3 py-2.5">
          <span className="text-xs font-medium">{t}</span>
          <span className="text-[10px] font-mono uppercase" style={{ color: c }}>{s}</span>
        </div>
      ))}
    </div>
    <div className="mt-4 flex items-end gap-1.5 h-16">
      {[30, 45, 38, 60, 52, 72, 80, 68].map((h, i) => (
        <div key={i} className="flex-1 rounded-t bg-gradient-to-t from-[#4f46e5] to-[#06b6d4]" style={{ height: `${h}%` }} />
      ))}
    </div>
  </div>
);

export const HeroCarousel = () => {
  const { content } = useSiteContent();
  const cms = (content?.hero_slides || []).filter((x) => x.title);
  const HERO_SLIDES = cms.length ? cms.map((x, k) => ({ ...DEFAULT_SLIDES[k % DEFAULT_SLIDES.length], ...Object.fromEntries(Object.entries(x).filter(([, v]) => v)) })) : DEFAULT_SLIDES;
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((x) => (x + 1) % HERO_SLIDES.length), 6500);
    return () => clearInterval(t);
  }, [HERO_SLIDES.length]);
  const s = HERO_SLIDES[i % HERO_SLIDES.length];
  return (
    <section className="relative overflow-hidden border-b" data-testid="hero-section">
      <AnimatePresence mode="wait">
        <motion.img key={s.img} src={s.img} alt="" initial={{ opacity: 0, scale: 1.05 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 1 }} className="absolute inset-0 h-full w-full object-cover" />
      </AnimatePresence>
      <div className="absolute inset-0 bg-[#f8fafc]/90 dark:bg-[#0b0f19]/90" />
      <div className="absolute inset-0 grain" />
      <div className="absolute -top-40 -left-32 h-[480px] w-[480px] rounded-full bg-[#7c3aed]/25 blur-[120px]" />
      <div className="absolute -bottom-40 right-0 h-[420px] w-[420px] rounded-full bg-[#06b6d4]/20 blur-[120px]" />
      <div className="container-x relative grid lg:grid-cols-12 gap-12 py-20 md:py-28 items-center min-h-[680px]">
        <div className="lg:col-span-7">
          <AnimatePresence mode="wait">
            <motion.div key={i} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.5 }}>
              <div className="inline-flex items-center gap-2 rounded-full border border-[#7c3aed]/30 bg-[#7c3aed]/10 px-3 py-1.5 mb-6">
                <Sparkles className="h-3.5 w-3.5 text-[#7c3aed]" />
                <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#6d28d9] dark:text-[#c4b5fd]">{s.eyebrow}</span>
              </div>
              <h1 data-testid="hero-headline" className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.03]">
                {s.title}<br /><span className="text-gradient">{s.accent}</span>
              </h1>
              <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-xl leading-relaxed">{s.copy}</p>
            </motion.div>
          </AnimatePresence>
          <div className="mt-9 flex flex-wrap gap-3">
            <Link to="/platform" data-testid="hero-explore-btn" className="btn-brand">Explore the Platform <ArrowRight className="h-4 w-4" /></Link>
            <Link to="/pricing" data-testid="hero-pricing-btn" className="btn-ghost">View Pricing</Link>
            <Link to="/tour" data-testid="hero-tour-btn" className="btn-ghost"><PlayCircle className="h-4 w-4" /> Product Tour</Link>
          </div>
          <p className="mt-5 flex items-center gap-2 text-sm text-muted-foreground"><CheckCircle2 className="h-4 w-4 text-[#10b981]" /> Explore features and sample workflows before choosing a plan. No subscription required to browse.</p>
          <div className="mt-10 flex gap-2" data-testid="hero-carousel-dots">
            {HERO_SLIDES.map((_, k) => (
              <button key={k} data-testid={`hero-dot-${k}`} onClick={() => setI(k)} aria-label={`Slide ${k + 1}`}
                className={`h-1.5 rounded-full transition-[width,background-color] duration-300 ${k === i ? "w-10 bg-[#7c3aed]" : "w-4 bg-foreground/20"}`} />
            ))}
          </div>
        </div>
        <div className="lg:col-span-5 relative flex justify-center lg:justify-end">
          <div className="animate-floaty"><MiniDash /></div>
          <div className="absolute -left-2 top-6 hidden md:flex glass rounded-xl px-3 py-2 items-center gap-2 shadow-xl"><CalendarDays className="h-4 w-4 text-[#7c3aed]" /><span className="text-xs font-semibold">2-week plan ready</span></div>
          <div className="absolute -bottom-4 left-10 hidden md:flex glass rounded-xl px-3 py-2 items-center gap-2 shadow-xl"><FileText className="h-4 w-4 text-[#db2777]" /><span className="text-xs font-semibold">12 drafts awaiting review</span></div>
          <div className="absolute -right-3 bottom-20 hidden md:flex glass rounded-xl px-3 py-2 items-center gap-2 shadow-xl"><BarChart3 className="h-4 w-4 text-[#10b981]" /><span className="text-xs font-semibold">Weekly report</span></div>
        </div>
      </div>
    </section>
  );
};
